import React, { useEffect, useRef } from "react";
import { Box, Container, Typography, Stack, Divider } from "@mui/material";
import { keyframes } from "@mui/system";
import Meeting from "../assets/Meeting.png";
import namamiLogo from "../assets/namami-logo.png";
import { Helmet } from "react-helmet";

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;



export default function AboutSection() {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.animationPlayState = 'running';
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us - Namami India Support Services</title>
        <meta
          name="description"
          content="Discover the inspiring journey of Namami India Support Services — an Indian-born company redefining facility management and hospitality through innovation, people, and purpose."
        />
        <meta
          name="keywords"
          content="Namami India, facility management, hospitality, engineering, housekeeping, maintenance, innovation, Odisha, India"
        />
        <meta name="author" content="Namami India Support Services" />
        <link rel="canonical" href="https://www.namamiindia.co.in/about" />
      </Helmet>

      {/* === Full Width Animated Hero Section === */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(-45deg, #dcabffff, #e3f2fd, #f8fbff, #e3f2fd)",
          backgroundSize: "400% 400%",
          animation: `${gradientShift} 15s ease infinite`,
          py: 8,
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #1565c0, #1e88e5)",
            opacity: 0.1,
            animation: `${floatAnimation} 6s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #004ba0, #1565c0)",
            opacity: 0.08,
            animation: `${floatAnimation} 8s ease-in-out infinite 1s`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "15%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #1e88e5, #42a5f5)",
            opacity: 0.06,
            animation: `${floatAnimation} 7s ease-in-out infinite 0.5s`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Stack alignItems="center" spacing={4} textAlign="center">
            {/* Logo with Animation */}
            <Box
              className="animate-on-scroll"
              component="img"
              src={namamiLogo}
              alt="Namami India Logo"
              sx={{
                width: { xs: 120, md: 160 },
                    }}
            />

            {/* Main Title */}
            <Typography
              className="animate-on-scroll"
              variant="h1"
              fontWeight={900}
              sx={{
                fontSize: { xs: "2.5rem", md: "4rem" },
                background: "linear-gradient(135deg, #1565c0 0%, #004ba0 50%, #0d47a1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: `${fadeInUp} 1s ease-out`,
                animationPlayState: 'paused',
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              About Namami India
            </Typography>

            {/* Tagline */}
            <Typography
              className="animate-on-scroll"
              variant="h4"
              fontWeight={400}
              sx={{
                color: "primary.main",
                maxWidth: 800,
                fontSize: { xs: "1.25rem", md: "1.75rem" },
                animation: `${fadeInUp} 1s ease-out 0.2s both`,
                animationPlayState: 'paused',
                opacity: 0.9,
                lineHeight: 1.4,
              }}
            >
              "From every building we manage to every team we empower — we believe service is not just a job, it's a way to make life better for others."
            </Typography>

            {/* Animated Scroll Indicator */}
            <Box
              className="animate-on-scroll"
              sx={{
                animation: `${floatAnimation} 2s ease-in-out infinite`,
                animationPlayState: 'paused',
                mt: 4,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 50,
                  border: "2px solid #1565c0",
                  borderRadius: 15,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 4,
                    height: 12,
                    backgroundColor: "#1565c0",
                    borderRadius: 2,
                    animation: `${floatAnimation} 2s ease-in-out infinite`,
                  }
                }}
              />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* === Content Section === */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(180deg, #ffffff, #f8fbff)",
          position: "relative",
        }}
      >
        {/* Decorative Shadow Elements */}
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: "10%",
            right: "10%",
            height: 100,
            background: "radial-gradient(ellipse at center, rgba(0,75,160,0.1) 0%, transparent 70%)",
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* === Intro Story === */}
          <Stack
            spacing={4}
            alignItems="center"
            className="animate-on-scroll"
            sx={{
              borderRadius: 4,
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 6 },
             
              mb: 6,
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="justify"
              sx={{ lineHeight: 1.9, fontSize: "1.1rem", maxWidth: 950 }}
            >
              <strong>Namami India Support Services Pvt. Ltd.</strong> was
              founded in <strong>2019</strong> with a single dream — to create a
              new standard of facility management in India that values
              precision, reliability, and human connection. What started as a
              small team of passionate professionals has today transformed into
              a nationwide network serving corporate houses, industries,
              institutions, and public infrastructure with a common goal:
              <em> making everyday operations smoother and safer.</em>
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="justify"
              sx={{ lineHeight: 1.9, fontSize: "1.1rem", maxWidth: 950 }}
            >
              We are more than just a service provider — we are enablers of
              growth. With over <strong>500+ skilled professionals</strong>,
              Namami India delivers excellence through integrated solutions that
              include <strong>facility management, engineering, housekeeping,
              catering, and project administration.</strong> Every partnership
              we form is guided by integrity, attention to detail, and a deep
              respect for our clients' vision. 
            </Typography>

           
              <Box
                component="img"
                src={Meeting}
                alt="Namami India Team"
                sx={{
                  width: { xs: "100%", sm: "85%", md: "70%" },
                  objectFit: "cover",
                  display: "block",
                     }}
              />
          

            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="justify"
              sx={{ lineHeight: 1.9, fontSize: "1.1rem", maxWidth: 950 }}
            >
              Our story is built on a foundation of trust — trust earned through
              consistent quality, transparent processes, and genuine care for
              people. Whether it's maintaining a corporate campus, running a
              canteen for thousands, or ensuring safety in an industrial unit,
              our goal remains the same: to provide a seamless experience that
              lets our clients focus on what they do best.
            </Typography>
          </Stack>

          {/* === MID HERO SECTION === */}
          <Box
            className="animate-on-scroll"
            sx={{
              animation: `${fadeInUp} 0.8s ease-out both`,
              animationPlayState: 'paused',
              mt: 12,
              py: { xs: 8, md: 10 },
              px: { xs: 3, md: 8 },
              background:
                "linear-gradient(135deg, #004ba0 0%, #1565c0 50%, #1e88e5 100%)",
              color: "white",
              textAlign: "center",
              borderRadius: 4,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grid\" width=\"10\" height=\"10\" patternUnits=\"userSpaceOnUse\"><path d=\"M 10 0 L 0 0 0 10\" fill=\"none\" stroke=\"rgba(255,255,255,0.1)\" stroke-width=\"0.5\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grid)\"/></svg>')",
              }
            }}
          >
            <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h3"
                fontWeight={800}
                mb={3}
                sx={{
                  fontSize: { xs: "1.9rem", md: "2.3rem" },
                  letterSpacing: 0.3,
                }}
              >
                Our Journey is a Story of People, Purpose, and Progress
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.15rem",
                  lineHeight: 1.9,
                  opacity: 0.95,
                  textAlign: "justify",
                }}
              >
                Every milestone we've achieved has been built on human effort
                and shared belief. From our first cleaning contract in a
                two-floor office to managing multi-site industrial facilities
                across India — every project tells a story of persistence,
                teamwork, and learning. We've seen our people grow from
                technicians to leaders, and our clients evolve from customers to
                partners. That's what makes Namami India not just a company, but
                a living ecosystem of people driven by a higher purpose.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.15rem",
                  lineHeight: 1.9,
                  mt: 3,
                  opacity: 0.95,
                  textAlign: "justify",
                }}
              >
                We blend technology and tradition — using data analytics,
                real-time reporting, and automation tools while staying grounded
                in the Indian ethos of sincerity and service. The result is a
                seamless balance between innovation and empathy — an approach
                that makes our services truly human-centered.
              </Typography>
            </Container>
          </Box>

          {/* === Mission & Vision === */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="center"
            alignItems="stretch"
            spacing={3}
            mt={10}
          >
            <Box
              className="animate-on-scroll"
              sx={{
                flex: 1,
                background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                textAlign: "justify",
                boxShadow: "0 8px 32px rgba(0,75,160,0.1)",
                animation: `${fadeInUp} 0.8s ease-out both`,
                animationPlayState: 'paused',
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 15px 40px rgba(0,75,160,0.15)",
                }
              }}
            >
              <Typography variant="h5" fontWeight={700} color="primary" mb={1.5}>
                Our Mission
              </Typography>
              <Divider sx={{ width: 60, borderColor: "primary.main", mb: 2 }} />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.9, fontSize: "1.1rem" }}
              >
                To create sustainable and smart environments where businesses
                and communities thrive. We aim to redefine India's facility
                management landscape by setting new standards in quality,
                safety, and innovation — driven by technology and powered by
                people.
              </Typography>
            </Box>

            <Box
              className="animate-on-scroll"
              sx={{
                flex: 1,
                background: "linear-gradient(135deg, #fff9e6, #ffffff)",
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                textAlign: "justify",
                boxShadow: "0 8px 32px rgba(255,152,0,0.1)",
                animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                animationPlayState: 'paused',
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 15px 40px rgba(255,152,0,0.15)",
                }
              }}
            >
              <Typography variant="h5" fontWeight={700} color="warning.main" mb={1.5}>
                Our Vision
              </Typography>
              <Divider sx={{ width: 60, borderColor: "warning.main", mb: 2 }} />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.9, fontSize: "1.1rem" }}
              >
                To become India's most admired facility management brand — known
                not only for operational excellence but also for creating a
                positive impact on society, empowering employees, and building
                sustainable systems for the future.
              </Typography>
            </Box>
          </Stack>

          {/* === Culture & Philosophy Section === */}
          <Box 
            className="animate-on-scroll"
            mt={10}
            sx={{
              animation: `${fadeInUp} 0.8s ease-out both`,
              animationPlayState: 'paused',
            }}
          >
            <Typography
              variant="h3"
              fontWeight={800}
              mb={3}
              textAlign="center"
              sx={{
                background: "linear-gradient(135deg, #1565c0, #004ba0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Our Culture & Philosophy
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="justify"
              sx={{
                lineHeight: 1.9,
                fontSize: "1.1rem",
                maxWidth: 950,
                mx: "auto",
              }}
            >
              At Namami India, culture is not written in a policy — it's lived
              every day. We encourage curiosity, responsibility, and respect.
              Every member of our team is empowered to take ownership of their
              work, ensuring that excellence becomes a collective habit, not an
              exception. We celebrate small wins, support each other during
              challenges, and stay grounded in our shared mission to serve with
              heart.
              <br />
              <br />
              What makes us unique is our blend of <strong>professional
              discipline and emotional intelligence.</strong> We understand that
              maintaining a facility isn't just about cleaning and maintenance —
              it's about creating a safe, inspiring space where people feel proud
              to belong.
            </Typography>
          </Box>

          {/* === Service Description Expanded === */}
          <Box 
            className="animate-on-scroll"
            mt={10}
            sx={{
              animation: `${fadeInUp} 0.8s ease-out both`,
              animationPlayState: 'paused',
            }}
          >
            <Typography
              variant="h3"
              fontWeight={800}
              mb={3}
              textAlign="center"
              sx={{
                background: "linear-gradient(135deg, #1565c0, #004ba0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              What We Offer
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="justify"
              sx={{
                lineHeight: 1.9,
                fontSize: "1.1rem",
                maxWidth: 950,
                mx: "auto",
              }}
            >
              From <strong>Integrated Facility Management</strong> to{" "}
              <strong>Hospitality & Catering</strong>, <strong>Engineering &
              Maintenance</strong>, and <strong>Administrative Support</strong>,
              Namami India provides everything your organization needs to run
              efficiently. Our teams operate across offices, factories, schools,
              hospitals, and public institutions — ensuring every corner of your
              facility reflects professionalism and care.
              <br />
              <br />
              Each service we deliver is designed to improve efficiency, reduce
              costs, and create a comfortable working atmosphere. We follow
              international standards while maintaining the warmth and
              flexibility of Indian service culture — a rare blend that our
              clients deeply appreciate.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
}